import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { Transaction, TransactionType, TransactionStatus, Wallet, WalletType } from '../types';
import { Logo } from '../components/shared/Logo';
import { LoadingSpinner } from '../components/shared/LoadingSpinner';
import { BottomNavigation } from '../components/navigation/BottomNavigation';
import api, { formatCurrency } from '../services/api';
import './WalletDetailScreen.css';

export const WalletDetailScreen: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { walletType } = useParams<{ walletType: string }>();
  const [wallet, setWallet] = useState<Wallet | null>(null);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [selectedFilters, setSelectedFilters] = useState<Set<TransactionType>>(new Set());
  const [isFiltersExpanded, setIsFiltersExpanded] = useState(false);

  // Get wallet data from navigation state or null if not provided
  const passedWallet = location.state?.wallet;

  useEffect(() => {
    const loadWalletData = async () => {
      setLoading(true);
      setError('');

      try {
        let walletData: Wallet;

        if (passedWallet) {
          // Use the wallet passed from navigation
          walletData = passedWallet;
          setWallet(walletData);
        } else {
          // Load wallets and find the matching one by type
          const wallets = await api.wallets.getWallets();
          const targetWalletType = walletType 
            ? walletType.charAt(0).toUpperCase() + walletType.slice(1).toLowerCase()
            : 'Main';
          const foundWallet = wallets.find(w => w.type === targetWalletType);
          
          if (!foundWallet) {
            setError(`${targetWalletType} wallet not found`);
            return;
          }
          
          walletData = foundWallet;
          setWallet(walletData);
        }

        // Load transactions for this specific wallet
        const walletTransactions = await api.transactions.getTransactions({
          walletId: walletData.id,
          limit: 50 // Load more transactions for the detail view
        });

        setTransactions(walletTransactions);

      } catch (err: any) {
        console.error('Failed to load wallet data:', err);
        setError('Failed to load wallet data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadWalletData();
  }, [walletType, passedWallet]);

  const getTransactionIcon = (type: TransactionType) => {
    switch (type) {
      case TransactionType.TOP_UP:
        return 'fas fa-plus-circle';
      case TransactionType.CREDIT:
        return 'fas fa-arrow-down';
      case TransactionType.DEBIT:
        return 'fas fa-arrow-up';
      case TransactionType.TRANSFER_IN:
        return 'fas fa-arrow-down';
      case TransactionType.TRANSFER_OUT:
        return 'fas fa-arrow-up';
      default:
        return 'fas fa-exchange-alt';
    }
  };

  const getTransactionColor = (type: TransactionType) => {
    switch (type) {
      case TransactionType.TOP_UP:
      case TransactionType.CREDIT:
      case TransactionType.TRANSFER_IN:
        return 'transaction-credit';
      case TransactionType.DEBIT:
      case TransactionType.TRANSFER_OUT:
        return 'transaction-debit';
      default:
        return 'transaction-neutral';
    }
  };

  const getStatusColor = (status: TransactionStatus) => {
    switch (status) {
      case TransactionStatus.PENDING:
        return 'status-pending';
      case TransactionStatus.PROCESSING:
        return 'status-processing';
      case TransactionStatus.COMPLETED:
        return 'status-completed';
      case TransactionStatus.FAILED:
        return 'status-failed';
      case TransactionStatus.CANCELLED:
        return 'status-cancelled';
      default:
        return 'status-pending';
    }
  };

  const getWalletIcon = (type: WalletType) => {
    switch (type) {
      case WalletType.MAIN:
        return 'fas fa-wallet';
      case WalletType.GRANT:
        return 'fas fa-gift';
      case WalletType.REWARDS:
        return 'fas fa-star';
      default:
        return 'fas fa-wallet';
    }
  };

  const getWalletColor = (type: WalletType) => {
    switch (type) {
      case WalletType.MAIN:
        return 'wallet-main';
      case WalletType.GRANT:
        return 'wallet-grant';
      case WalletType.REWARDS:
        return 'wallet-rewards';
      default:
        return 'wallet-main';
    }
  };


  const formatDate = (date: Date | string) => {
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (dateObj.toDateString() === today.toDateString()) {
      return `Today, ${dateObj.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}`;
    } else if (dateObj.toDateString() === yesterday.toDateString()) {
      return `Yesterday, ${dateObj.toLocaleTimeString('en-ZA', { hour: '2-digit', minute: '2-digit' })}`;
    } else {
      return dateObj.toLocaleDateString('en-ZA', {
        day: 'numeric',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    }
  };

  const handleFilterToggle = (type: TransactionType) => {
    setSelectedFilters(prev => {
      const newFilters = new Set(prev);
      if (newFilters.has(type)) {
        newFilters.delete(type);
      } else {
        newFilters.add(type);
      }
      return newFilters;
    });
  };

  const handleSelectAll = () => {
    setSelectedFilters(new Set());
  };

  const handleClearFiltersAndCollapse = () => {
    setSelectedFilters(new Set());
    setIsFiltersExpanded(false);
  };

  const getFilterDisplayText = () => {
    if (selectedFilters.size === 0) {
      return `All (${transactions.length})`;
    } else if (selectedFilters.size === 1) {
      const type = Array.from(selectedFilters)[0];
      const count = transactions.filter(transaction => transaction.type === type).length;
      const typeName = type.toLowerCase().replace('_', ' ');
      return `${typeName.charAt(0).toUpperCase() + typeName.slice(1)} (${count})`;
    } else {
      const totalCount = transactions.filter(transaction => selectedFilters.has(transaction.type)).length;
      return `${selectedFilters.size} Filters (${totalCount})`;
    }
  };

  const filteredTransactions = selectedFilters.size === 0 
    ? transactions 
    : transactions.filter(transaction => selectedFilters.has(transaction.type));

  if (loading) {
    return (
      <div className="wallet-detail-screen">
        <LoadingSpinner message="Loading wallet details..." />
      </div>
    );
  }

  if (error || !wallet) {
    return (
      <div className="wallet-detail-screen">
        <div className="wallet-detail-container">
          <div className="screen-header">
            <div className="header-left">
              <button onClick={() => navigate('/dashboard')} className="back-button">
                <i className="fas fa-arrow-left"></i>
              </button>
              <h1>Wallet Details</h1>
            </div>
            <div className="header-right">
              <Logo size="small" variant="light" />
            </div>
          </div>
          <div className="screen-content">
            <div className="error-container">
              <h2>Error Loading Wallet</h2>
              <p>{error || 'Wallet not found'}</p>
              <button onClick={() => navigate('/dashboard')}>Back to Dashboard</button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="wallet-detail-screen">
      <div className="wallet-detail-container">
        <div className="screen-header">
          <div className="header-left">
            <button onClick={() => navigate('/dashboard')} className="back-button">
              <i className="fas fa-arrow-left"></i>
            </button>
            <h1>{wallet.type} Wallet</h1>
          </div>
          <div className="header-right">
            <Logo size="small" variant="light" />
          </div>
        </div>

        <div className="screen-content">
          {/* Wallet Summary */}
          <div className="wallet-summary">
            <div className={`wallet-summary-card ${getWalletColor(wallet.type)}`}>
              <div className="wallet-icon">
                <i className={getWalletIcon(wallet.type)}></i>
              </div>
              <div className="wallet-info">
                <h2>{wallet.type} Wallet</h2>
                <div className="balance-amount">
                  {formatCurrency(wallet.totalBalance)}
                </div>
                {wallet.reservedAmount > 0 && (
                  <div className="available-balance">
                    Available: {formatCurrency(wallet.availableBalance)}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="filter-section">
            <button 
              className="filter-toggle-button"
              onClick={() => setIsFiltersExpanded(!isFiltersExpanded)}
            >
              <div className="filter-toggle-content">
                <i className="fas fa-filter"></i>
                <span>Filter Transactions</span>
                <span className="filter-count">
                  {getFilterDisplayText()}
                </span>
              </div>
              <i className={`fas fa-chevron-${isFiltersExpanded ? 'up' : 'down'} filter-toggle-icon`}></i>
            </button>
            
            <div className={`transaction-filters ${isFiltersExpanded ? 'expanded' : 'collapsed'}`}>
              <button 
                className={`filter-button ${selectedFilters.size === 0 ? 'active' : ''}`}
                onClick={handleSelectAll}
              >
                <span>All</span>
                <span className="filter-badge">{transactions.length}</span>
              </button>
              <button 
                className={`filter-button ${selectedFilters.has(TransactionType.TOP_UP) ? 'active' : ''}`}
                onClick={() => handleFilterToggle(TransactionType.TOP_UP)}
              >
                <div className="filter-button-content">
                  <i className={`fas fa-${selectedFilters.has(TransactionType.TOP_UP) ? 'check-square' : 'square'} filter-checkbox`}></i>
                  <span>Top Up</span>
                </div>
                <span className="filter-badge">{transactions.filter(t => t.type === TransactionType.TOP_UP).length}</span>
              </button>
              <button 
                className={`filter-button ${selectedFilters.has(TransactionType.DEBIT) ? 'active' : ''}`}
                onClick={() => handleFilterToggle(TransactionType.DEBIT)}
              >
                <div className="filter-button-content">
                  <i className={`fas fa-${selectedFilters.has(TransactionType.DEBIT) ? 'check-square' : 'square'} filter-checkbox`}></i>
                  <span>Payments</span>
                </div>
                <span className="filter-badge">{transactions.filter(t => t.type === TransactionType.DEBIT).length}</span>
              </button>
              <button 
                className={`filter-button ${selectedFilters.has(TransactionType.CREDIT) ? 'active' : ''}`}
                onClick={() => handleFilterToggle(TransactionType.CREDIT)}
              >
                <div className="filter-button-content">
                  <i className={`fas fa-${selectedFilters.has(TransactionType.CREDIT) ? 'check-square' : 'square'} filter-checkbox`}></i>
                  <span>Credits</span>
                </div>
                <span className="filter-badge">{transactions.filter(t => t.type === TransactionType.CREDIT).length}</span>
              </button>
              <button 
                className={`filter-button ${selectedFilters.has(TransactionType.TRANSFER_IN) ? 'active' : ''}`}
                onClick={() => handleFilterToggle(TransactionType.TRANSFER_IN)}
              >
                <div className="filter-button-content">
                  <i className={`fas fa-${selectedFilters.has(TransactionType.TRANSFER_IN) ? 'check-square' : 'square'} filter-checkbox`}></i>
                  <span>Transfer In</span>
                </div>
                <span className="filter-badge">{transactions.filter(t => t.type === TransactionType.TRANSFER_IN).length}</span>
              </button>
              <button 
                className={`filter-button ${selectedFilters.has(TransactionType.TRANSFER_OUT) ? 'active' : ''}`}
                onClick={() => handleFilterToggle(TransactionType.TRANSFER_OUT)}
              >
                <div className="filter-button-content">
                  <i className={`fas fa-${selectedFilters.has(TransactionType.TRANSFER_OUT) ? 'check-square' : 'square'} filter-checkbox`}></i>
                  <span>Transfer Out</span>
                </div>
                <span className="filter-badge">{transactions.filter(t => t.type === TransactionType.TRANSFER_OUT).length}</span>
              </button>
              
              {selectedFilters.size > 0 && (
                <button 
                  className="clear-filters-button"
                  onClick={handleClearFiltersAndCollapse}
                >
                  <i className="fas fa-times"></i>
                  <span>Clear Filters</span>
                </button>
              )}
            </div>
          </div>

          {/* Transactions List */}
          {filteredTransactions.length === 0 ? (
            <div className="empty-state">
              <i className="fas fa-receipt"></i>
              <h3>No transactions found</h3>
              <p>
                {selectedFilters.size === 0 
                  ? `No transactions in your ${wallet.type.toLowerCase()} wallet yet.`
                  : selectedFilters.size === 1
                  ? `No ${Array.from(selectedFilters)[0].toLowerCase().replace('_', ' ')} transactions found in this wallet.`
                  : "No transactions match the selected filters."
                }
              </p>
            </div>
          ) : (
            <div className="transactions-list">
              {filteredTransactions.map(transaction => (
                <div key={transaction.id} className="transaction-card">
                  <div className="transaction-header">
                    <div className="transaction-left">
                      <div className="transaction-icon-container">
                        <div className={`transaction-icon ${getTransactionColor(transaction.type)}`}>
                          <i className={getTransactionIcon(transaction.type)}></i>
                        </div>
                      </div>
                      <div className="transaction-info">
                        <h4>{transaction.description}</h4>
                        <p className="transaction-description">
                          {wallet.type} Wallet Transaction
                        </p>
                      </div>
                    </div>
                    <div className={`transaction-status ${getStatusColor(transaction.status)}`}>
                      {transaction.status}
                    </div>
                  </div>
                  
                  <div className="transaction-details">
                    <div>
                      <span className="amount-label">Amount</span>
                      <span className={`amount-value ${getTransactionColor(transaction.type) === 'transaction-credit' ? 'credit' : 'debit'}`}>
                        {(transaction.type === TransactionType.CREDIT || transaction.type === TransactionType.TOP_UP || transaction.type === TransactionType.TRANSFER_IN) ? '+' : '-'}
                        {formatCurrency(Math.abs(transaction.amount))}
                      </span>
                    </div>
                    <div>
                      <span className="date-label">Date</span>
                      <span className="date-value">{formatDate(transaction.timestamp || transaction.createdAt)}</span>
                    </div>
                    {transaction.reference && (
                      <div>
                        <span className="reference-label">Reference</span>
                        <span className="reference-value">{transaction.reference}</span>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
      <BottomNavigation />
    </div>
  );
};